import { List } from 'immutable'
import * as React from 'react'
import { AsyncState, IOWidget, NavLink, Updater, async, fromJSX, loadingAsyncState } from 'widgets-for-react'
import { PublicSiteState } from '../../../../../../../root/state'
import { CMSState, Link } from '../../../../cms/blocks/types.gen'
import { getAllUpdates, getOverview } from '../../../../drupal/api/api.mod'
import { parseImageUrl } from '../../../../drupal/rendering/image_utils.mod'
import { PaginatedItem } from '../../../../drupal/types_and_parser/extra_types.mod'
import { ParsedNode } from '../../../../drupal/types_and_parser/types.ext.gen'
import { getDrupalLanguage } from '../../../application.mod'
import { AbstractParsedUpdate } from '../../../custom_pages/shared/AbstractParsedUpdate'
import { loader } from '../../../templates/loader.mod'
import { getLangFromUrl } from '../../../utils/history.mod'
import moment = require('moment')

export type relatedContentBlockProps = {
  block_index: number
  title: string
  link: Link[]
  allUpdates: AsyncState<PaginatedItem<AbstractParsedUpdate>>
  nodes: AsyncState<PaginatedItem<ParsedNode>>
  max_nodes: number
}

type Bundle = ParsedNode['bundle']

export type relatedContentBlockStyle = {}

export function relatedContentBlock(
  props: relatedContentBlockProps,
  options?: { style?: relatedContentBlockStyle; key?: string }
): IOWidget<PublicSiteState, Updater<CMSState>> {
  return (public_state: PublicSiteState) =>
    fromJSX((setState) => {
      // State updater
      const setBlockState =
        <K extends keyof relatedContentBlockProps>(prop: K, newValue: relatedContentBlockProps[K]) =>
        (s: CMSState): CMSState => {
          if (!s.blocks.has(props.block_index)) return s
          let current_block = s.blocks.get(props.block_index) as any
          if (current_block.kind != 'relatedContentBlock') return s
          current_block = {
            ...current_block,
            value: [
              {
                ...current_block.value[0],
                [prop]: newValue
              },
              current_block.value[1]
            ]
          }
          return { ...s, blocks: s.blocks.set(props.block_index, current_block) }
        }

      // Set block promises
      let page_bundle: Bundle = 'page' // Based on the content type we choose different api loaders
      if (public_state.page.page.kind == 'loaded') {
        page_bundle = public_state.page.page.value.extra_data.node.bundle
      }

      let nodes = props.nodes
      let allUpdates = props.allUpdates
      let labels = []
      if (
        public_state.page.page.kind == 'loaded' &&
        (public_state.page.page.value.extra_data.node.bundle == 'update' ||
          public_state.page.page.value.extra_data.node.bundle == 'expertise' ||
          public_state.page.page.value.extra_data.node.bundle == 'page' ||
          public_state.page.page.value.extra_data.node.bundle == 'case' ||
          public_state.page.page.value.extra_data.node.bundle == 'project')
      ) {
        labels = public_state.page.page.value.extra_data.node.field_category.map((elem) => elem.id).toArray()
      }

      let allowed_bundles: Bundle[] = ['update', 'expertise', 'page', 'case', 'project'] // These are the page types the related content block is shown
      if (allowed_bundles.includes(page_bundle) && nodes.kind == 'unloaded') {
        nodes = loadingAsyncState(() =>
          getOverview<ParsedNode>('related-content-load-more', getLangFromUrl(), null, List(labels), true)
        )
        setState((s1) => setBlockState('nodes', nodes)(s1))
      } else if (!allowed_bundles.includes(page_bundle) && allUpdates.kind == 'unloaded') {
        // For any other case render all updates
        allUpdates = loadingAsyncState(() => getAllUpdates(getLangFromUrl()))
        setState((s1) => setBlockState('allUpdates', allUpdates)(s1))
      }

      // Set related content array
      let current_page_id = -1
      let current_title = ''
      if (public_state.page.page.kind == 'loaded') {
        current_page_id = public_state.page.page.value.extra_data.node.id
        current_title = public_state.page.page.value.extra_data.node.label
      }
      let relatedContent: Array<Omit<RelatedContentItem, 'index'>> = []

      // Add updates - only when not in allowed bundles
      if (props.allUpdates.kind == 'loaded') {
        relatedContent = relatedContent.concat(
          props.allUpdates.value.items
            .filter((node) => node != null && node.title != current_title)
            .map<Omit<RelatedContentItem, 'index'>>((update, i) => ({
              id: i,
              title: update?.title,
              link: update.view_node,
              categories: update?.field_category.split(','),
              content_type: 'update',
              date: update.created,
              image_url: update?.field_media_image
            }))
        )
      }
      // Add updates
      if (props.nodes.kind == 'loaded') {
        relatedContent = relatedContent.concat(
          props.nodes.value.items
            .filter((node) => node != null && node.id != current_page_id)
            .map<Omit<RelatedContentItem, 'index'>>((update) => ({
              id: update.id,
              title: update?.label,
              link: update.language_links.get(getDrupalLanguage(getLangFromUrl())),
              categories:
                update.bundle == 'update' ||
                update.bundle == 'page' ||
                update.bundle == 'case' ||
                update.bundle == 'expertise' ||
                update.bundle == 'project' ||
                update.bundle == 'career'
                  ? update?.field_category.map((x) => x.label).toArray()
                  : [],
              content_type: update.bundle,
              date: update.created.toString(),
              image_url: parseImageUrl(update?.field_introduction_image?.thumbnail?.image?.style_urls?.grid)
            }))
        )
      }

      const INCREASE_NODES = 5 // The initial load is set in application.mod.ts in max_nodes
      let sliced_content = relatedContent.slice(0, props.max_nodes)

      return (
        <>
          {async(props.allUpdates).run((a) => setState(setBlockState('allUpdates', a(props.allUpdates))))}
          {async(props.nodes).run((a) => setState(setBlockState('nodes', a(props.nodes))))}

          <section className="block block--related block--related-xl ls-block-spacing">
            <div className="block__inner ls-container">
              <header className="block__header">
                <h2 className="block__title title--l">{props.title ? props.title : 'Related content'}</h2>
              </header>
              <div className="block__content">
                <ul className="block__list grid">
                  {sliced_content.map((node, i) => (
                    <RelatedContentItem key={node.id} {...node} index={i} />
                  ))}
                  {(props.nodes.kind == 'loading' || props.allUpdates.kind == 'loading') && loader.run((_) => {})}
                </ul>
              </div>
              {props.max_nodes < relatedContent.length && (
                <footer className="block__footer">
                  <button
                    onClick={() => setState(setBlockState('max_nodes', props.max_nodes + INCREASE_NODES))}
                    className="link"
                  >
                    Load more
                  </button>
                </footer>
              )}
            </div>
          </section>
        </>
      )
    })
}

interface RelatedContentItem {
  index: number
  id: number
  title: string
  content_type: Bundle
  date: string
  categories: string[]
  image_url: string
  link: string
}

const RelatedContentItem = (props: RelatedContentItem) => {
  let date = props.date ? moment(props.date, 'ddd MMMM DD YYYY', true) : undefined

  if (date != undefined && !date.isValid()) date = moment(props.date)
  return (
    <li className="grid__item">
      <article className="grid__article">
        <div className="grid__content">
          <header className="grid__header">
            {props.title && <h3 className="grid__title title--s">{props.title}</h3>}
            <NavLink className="grid__link" to={props.link} />
          </header>
          {props.content_type == 'update' && date && props.index != 0 && (
            <time className="grid__date text--s" dateTime={date.format('D MMMM YYYY')}>
              {date.format('D MMMM YYYY')}
            </time>
          )}
        </div>
        <div className="grid__wrap">
          <div className="grid__tags tags">
            {props.categories &&
              props.categories.map((cat, i) => (
                <span key={i} className="grid__tag tag tag--m">
                  {cat}
                </span>
              ))}
            <span className="grid__tag tag tag--m">{props.content_type}</span>
          </div>

          <div className="grid__visual fit-object">
            {props.image_url && <img loading="lazy" className="grid__image" src={props.image_url} alt="" />}
          </div>
        </div>
      </article>
    </li>
  )
}
